import type { Path, UseFormReturn } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useDebounceCallback } from 'usehooks-ts';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateAppSettingsRequestBody } from '~/lib/api/modules/AppSettings';
import type { IGeneralSettingsFormValues } from './GeneralSettingsForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useApi } from '~/lib/api';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { GeneralSettingsForm } from './GeneralSettingsForm';

const DEBOUNCE_DELAY = 1000;

export type IUpdateSettingsFormProps = IContainerProps & {
  appId: string;
};

export const UpdateGeneralSettingsForm: React.FC<IUpdateSettingsFormProps> = (
  props,
) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const appQuery = useAppQuery({ appId }, { cacheTime: 0 });
  const app = appQuery.data;
  const pressSettings = app?.settings?.press;

  const [submittingFields, setSubmittingFields] = useState<
    Array<Path<IGeneralSettingsFormValues>>
  >([]);

  const handleChange = (
    form: UseFormReturn<IGeneralSettingsFormValues>,
    updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
  ): void => {
    if (!pressSettings) {
      return;
    }

    const values = form.getValues();
    const updatedData: IApiUpdateAppSettingsRequestBody = {
      ...app.settings,
      press: {
        ...pressSettings,
        env: {
          ...pressSettings?.env,
          // Users
          registerWithCrowdaa: values.registerWithCrowdaa,
          signInWithCrowdaa: values.signInWithCrowdaa,
          loginWithUsername: values.loginWithUsername === 'true',
          signInWithFacebook: values.signInWithFacebook,
          signInWithApple: values.signInWithApple,
          geolocation: values.geolocation,

          // All articles
          loginArticleRequired: values.loginArticleRequired,
          displayArticleAuthor: values.displayArticleAuthor,
          displayArticleCommentsCount: values.displayArticleCommentsCount,
          displayArticleLikesViews: values.displayArticleLikesViews,
          displayPopularCategories: values.displayPopularCategories,
          displayTabsNames: values.displayTabsNames,

          // Admin articles
          feedArticleCommentsEnabled: values.feedArticleCommentsEnabled,
          feedArticleShareEnabled: values.feedArticleShareEnabled,

          // Guest articles
          communityArticleCommentsEnabled:
            values.communityArticleCommentsEnabled,
          communityArticleShareEnabled: values.communityArticleShareEnabled,
        },
        // All articles
        aiModerationEnabled: values.aiModerationEnabled,
        moderationRequired: values.moderationRequired,

        // Misc
        chatNotificationsEnabled: values?.chatNotificationsEnabled,
      },
    };

    void api.appSettings
      .update(appId, updatedData)
      .catch(() =>
        updatedFields?.forEach((field) =>
          form.setValue(field, !form.getValues(field)),
        ),
      )
      .finally(() => {
        setSubmittingFields((submittingFields) =>
          submittingFields.filter((f) => !updatedFields?.includes(f)),
        );

        void queryClient.invalidateQueries({ queryKey: [appId, 'settings'] });
      });
  };

  const handleChangeDebounced = useDebounceCallback(
    handleChange,
    DEBOUNCE_DELAY,
  );

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title
        supportingText={t('UpdateGeneralSettingsForm.headline')}
      >
        {t('UpdateGeneralSettingsForm.title')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
      >
        {app ? (
          <GeneralSettingsForm
            appId={appId}
            defaultValues={{
              signInWithAppleEnabled: !!app?.credentials?.apple?.clientId,
              chatEnabled: !!app?.credentials?.firebase?.webAppConfig,
              signInWithFacebookEnabled: false,

              // Users
              usersEnabled:
                app.settings?.press?.env?.registerWithCrowdaa ||
                pressSettings?.env?.signInWithCrowdaa ||
                pressSettings?.env?.signInWithFacebook ||
                pressSettings?.env?.signInWithApple ||
                pressSettings?.env?.geolocation,
              registerWithCrowdaa: pressSettings?.env?.registerWithCrowdaa,
              signInWithCrowdaa: pressSettings?.env?.signInWithCrowdaa,
              loginWithUsername: pressSettings?.env?.loginWithUsername
                ? 'true'
                : 'false',
              signInWithFacebook: pressSettings?.env?.signInWithFacebook,
              signInWithApple: pressSettings?.env?.signInWithApple,
              geolocation: pressSettings?.env?.geolocation,

              // All articles
              loginArticleRequired: pressSettings?.env?.loginArticleRequired,
              displayArticleAuthor: pressSettings?.env?.displayArticleAuthor,
              displayArticleCommentsCount:
                pressSettings?.env?.displayArticleCommentsCount,
              moderationRequired: pressSettings?.moderationRequired,
              displayArticleLikesViews:
                pressSettings?.env?.displayArticleLikesViews,
              displayPopularCategories:
                pressSettings?.env?.displayPopularCategories,
              displayTabsNames: pressSettings?.env?.displayTabsNames,

              // Admin articles
              feedArticleCommentsEnabled:
                pressSettings?.env?.feedArticleCommentsEnabled,
              feedArticleShareEnabled:
                pressSettings?.env?.feedArticleShareEnabled,

              // Guest articles
              communityArticleCommentsEnabled:
                pressSettings?.env?.communityArticleCommentsEnabled,
              communityArticleShareEnabled:
                pressSettings?.env?.communityArticleShareEnabled,

              // Misc
              chatNotificationsEnabled: pressSettings?.chatNotificationsEnabled,
            }}
            onChange={(
              form: UseFormReturn<IGeneralSettingsFormValues>,
              updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
            ) => {
              setSubmittingFields((submittingFields) => [
                ...submittingFields,
                ...(updatedFields ?? []),
              ]);

              handleChangeDebounced(form, updatedFields);
            }}
            submittingFields={submittingFields}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
